/**
 * The three sizes the Notification Center takes above the composer: the pill,
 * the card with the first need-attention rows, and the full center.
 */
export type AlertsCenterPresentation = 'collapsed' | 'truncated' | 'full';

/** Rows the truncated card shows — the first need-attention rows of the full center. */
export const TRUNCATED_ROW_LIMIT = 3;

/** The pill's fixed height (`h-[34px]`, border included). */
const PILL_HEIGHT_PX = 34;
/** The card's 1px border, top and bottom. */
const CARD_BORDER_PX = 2;
/** The card's vertical padding (`py-4`), top and bottom. */
const CARD_PADDING_PX = 16;
/** The header row — the chevron button's `size-9` sets it. */
const HEADER_HEIGHT_PX = 36;
/** The card's `gap-2`: header to rows, rows to "View all". */
const CARD_GAP_PX = 8;
/**
 * `AlertListItem`'s standard row height as the app renders it: the design's 84
 * plus the app type scale's taller title and subtitle lines (an accepted delta),
 * border included.
 */
const ROW_HEIGHT_PX = 92;
/** `AlertSection`'s `gap-2` between rows. */
const ROW_GAP_PX = 8;
/** The "View all" button (`h-10`). */
const VIEW_ALL_HEIGHT_PX = 40;

export interface IAlertsCardHeightInput {
  presentation: AlertsCenterPresentation;
  /** The household's ACTIVE total from the counts read. */
  activeCount: number;
  /** Need-attention rows fetched so far, before the truncated card's cut. */
  needAttentionRowCount: number;
}

/**
 * The height the composer reserves under the Notification Center card, in
 * pixels. Deterministic rather than measured: every part of the pill and the
 * truncated card has a fixed height, so the composer can hold the card's space
 * without observing it.
 *
 * Zero exactly when `AlertsCenterBase` renders nothing — the pill at a count
 * of zero, the truncated card with no need-attention rows — and always zero
 * for the full center, which overlays the pane instead of taking space in it.
 */
export function getAlertsCardHeightPx({
  presentation,
  activeCount,
  needAttentionRowCount,
}: IAlertsCardHeightInput): number {
  if (presentation === 'full') {
    return 0;
  }

  if (presentation === 'collapsed') {
    return Math.max(activeCount, needAttentionRowCount) === 0 ? 0 : PILL_HEIGHT_PX;
  }

  if (needAttentionRowCount === 0) {
    return 0;
  }

  const rows = Math.min(needAttentionRowCount, TRUNCATED_ROW_LIMIT);
  const rowsHeight = rows * ROW_HEIGHT_PX + (rows - 1) * ROW_GAP_PX;

  return (
    CARD_BORDER_PX +
    CARD_PADDING_PX +
    HEADER_HEIGHT_PX +
    CARD_GAP_PX +
    rowsHeight +
    CARD_GAP_PX +
    VIEW_ALL_HEIGHT_PX +
    CARD_PADDING_PX
  );
}

/** Which side of a presentation change an element should wear this frame. */
export type AlertsCenterMotionStage = 'pre' | 'active';

/**
 * A presentation change in flight. `from` is the presentation being left;
 * `pre` wears its styles for one frame so the browser has a start state, and
 * `active` wears the target's. Absent at rest.
 */
export interface IAlertsCenterMotion {
  from: AlertsCenterPresentation;
  stage: AlertsCenterMotionStage;
  /** The pixel height the card is leaving; null when `from` is `full`, whose height the pane measures. */
  fromHeightPx: number | null;
  /** The pixel height the card is growing or shrinking to; null for a `full` target. */
  toHeightPx: number | null;
}

/** The presentation whose styles an element wears this frame — the outgoing one at `pre`, the target otherwise. */
export function getAlertsCenterStyledPresentation(
  presentation: AlertsCenterPresentation,
  motion: IAlertsCenterMotion | undefined
): AlertsCenterPresentation {
  return motion?.stage === 'pre' ? motion.from : presentation;
}

/** How the pane positions the card's overlay: above the composer, or over the whole pane. */
export type AlertsOverlayAnchor = 'composer' | 'pane';

export interface IAlertsOverlayFrame {
  anchor: AlertsOverlayAnchor;
  /** An explicit pixel height while the card moves; null at rest, when the card takes its natural (or pane-filling) height. */
  heightPx: number | null;
}

export interface IAlertsOverlayFrameInput {
  presentation: AlertsCenterPresentation;
  motion: IAlertsCenterMotion | undefined;
  /** The pane's inner height, less its insets: the full center's pixel endpoint. Null until measured. */
  paneInnerHeightPx: number | null;
}

/**
 * The overlay's geometry for this frame. At rest the card is anchored by its
 * presentation with no explicit height, so the full center follows a viewport
 * resize and a composer card takes its natural height. While moving, `pre`
 * pins the outgoing presentation's anchor and pixel height — a value equal to
 * what is already on screen, so nothing jumps — and `active` writes the
 * target's, which is what the browser transitions to.
 */
export function resolveAlertsOverlayFrame({
  presentation,
  motion,
  paneInnerHeightPx,
}: IAlertsOverlayFrameInput): IAlertsOverlayFrame {
  const styledAs = getAlertsCenterStyledPresentation(presentation, motion);
  const anchor: AlertsOverlayAnchor = styledAs === 'full' ? 'pane' : 'composer';

  if (!motion) {
    return { anchor, heightPx: null };
  }

  const heightPx = motion.stage === 'pre' ? motion.fromHeightPx : motion.toHeightPx;
  return { anchor, heightPx: styledAs === 'full' ? paneInnerHeightPx : heightPx };
}
